import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {httpGetCities, HttpGetCitiesOptions} from './address.http'
import {z} from 'zod'
import {City, PaginationResponse} from '@/types'

export const HttpGetCitiesResponse = z.object({
    data: z.array(City),
    meta: PaginationResponse
})
export type HttpGetCitiesResponse = z.infer<typeof HttpGetCitiesResponse>

export const useGetCities = (
    promiseArgs: Omit<HttpGetCitiesOptions, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetCitiesResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.CITIES, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetCities({
                    page: pageParam,
                    search: searchValue || promiseArgs.search,
                    limit: promiseArgs.limit,
                    responseType: promiseArgs.responseType
                }),
                responseShape: HttpGetCitiesResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
